import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Company reimbursement policy: standard categories, accounting email, payment timing, mileage.
export const ReimbursementPolicyCard = ({ token, policy, onSaved }) => {
  const { toast } = useToast();
  const [p, setP] = useState(policy || {});
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/reimbursements/policy`, patch, h);
      setP((old) => ({ ...old, ...r.data }));
      onSaved?.(r.data);
    } catch (e) {
      toast({ title: 'Could not save policy', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  const addCat = () => {
    const c = newCat.trim();
    if (!c) return;
    save({ categories: [...(p.categories || []), c] });
    setNewCat('');
  };

  return (
    <div className="ht-card p-4 space-y-4" data-testid="reimb-policy-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Reimbursement policy</h3>
          <p className="text-xs text-slate-400 mt-0.5">Company standard applied to every claim. Workers only pick a type, add the receipt and the amount.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">Enabled <Switch checked={!!p.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="reimb-policy-enabled" /></div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Reimbursement types (dropdown workers see)</Label>
        <div className="flex flex-wrap gap-2" data-testid="reimb-policy-categories">
          {(p.categories || []).map((c) => (
            <span key={c} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#0F1923] border border-[#1F2D3F] text-slate-200" data-testid={`reimb-policy-cat-${c.replace(/\W+/g, '-').toLowerCase()}`}>
              {c}
              <button type="button" className="text-slate-500 hover:text-rose-400" disabled={saving || (p.categories || []).length <= 1} onClick={() => save({ categories: p.categories.filter((x) => x !== c) })} aria-label={`Remove ${c}`} data-testid={`reimb-policy-cat-remove-${c.replace(/\W+/g, '-').toLowerCase()}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Add a type, e.g. Safety gear" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCat()} className="h-9" data-testid="reimb-policy-new-cat" />
          <Button size="sm" variant="outline" onClick={addCat} disabled={saving || !newCat.trim()} data-testid="reimb-policy-add-cat"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1 md:col-span-1">
          <Label className="text-xs">Accounting / bookkeeper email</Label>
          <Input type="email" placeholder="books@company.com" defaultValue={p.accounting_email || ''} disabled={saving}
            onBlur={(e) => { if (e.target.value.trim() !== (p.accounting_email || '')) save({ accounting_email: e.target.value.trim() }); }} data-testid="reimb-policy-email" />
          <p className="text-[11px] text-slate-500">Approved claims (with the receipt attached) are emailed here for payment.</p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Payment timing</Label>
          <Select value={p.payment_method || 'next_pay_run'} onValueChange={(v) => save({ payment_method: v })}>
            <SelectTrigger data-testid="reimb-policy-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="next_pay_run" data-testid="reimb-policy-method-next_pay_run">On the next pay run (statement line)</SelectItem>
              <SelectItem value="deposit_24h" data-testid="reimb-policy-method-deposit_24h">Deposit within 24 hours</SelectItem>
              <SelectItem value="processing_days" data-testid="reimb-policy-method-processing_days">Allow N processing days</SelectItem>
            </SelectContent>
          </Select>
          {p.payment_method === 'processing_days' && (
            <Input type="number" min={1} max={60} defaultValue={p.processing_days || 5} className="h-9 mt-1" disabled={saving}
              onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v && v !== p.processing_days) save({ processing_days: v }); }} data-testid="reimb-policy-days" />
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Options</Label>
          <label className="flex items-center justify-between text-xs text-slate-300"><span>Workers may request immediate payment</span><Switch checked={!!p.allow_immediate_request} disabled={saving} onCheckedChange={(v) => save({ allow_immediate_request: v })} data-testid="reimb-policy-immediate" /></label>
          <label className="flex items-center justify-between text-xs text-slate-300"><span>Receipt photo required for purchases</span><Switch checked={!!p.require_receipt} disabled={saving} onCheckedChange={(v) => save({ require_receipt: v })} data-testid="reimb-policy-receipt" /></label>
          <label className="flex items-center justify-between text-xs text-slate-300"><span>Mileage claims</span><Switch checked={!!p.mileage_enabled} disabled={saving} onCheckedChange={(v) => save({ mileage_enabled: v })} data-testid="reimb-policy-mileage" /></label>
          {p.mileage_enabled && (
            <div className="flex items-center gap-2 text-xs text-slate-300">Rate per km $
              <Input type="number" min={0} max={5} step={0.01} defaultValue={p.mileage_rate ?? 0.7} className="h-8 w-24" disabled={saving}
                onBlur={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== p.mileage_rate) save({ mileage_rate: v }); }} data-testid="reimb-policy-rate" />
            </div>
          )}
        </div>
      </div>
      {saving && <p className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>}
    </div>
  );
};
